import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { db } from '../../firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../auth/AuthContext';

function PlansPanel() {
  const [userSubscription, setUserSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!user?.uid) {
        console.log('No user found:', user);
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching subscription for user:', user.uid);
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          console.log('Firestore user data:', data);
          setUserSubscription(data.subscription);
        } else {
          console.log('No user document found');
          setUserSubscription(null);
        }
      } catch (err) {
        console.error('Error fetching subscription:', err);
        setUserSubscription(null);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [user]);

  const handleCancelSubscription = async () => {
    if (!user?.uid) return;
    
    try {
      const userDocRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userDocRef);
      const subscriptionId = userDoc.data()?.stripeSubscriptionId;

      if (!subscriptionId) {
        throw new Error('No subscription ID found');
      }

      await axios.post('/api/cancel-subscription', { subscriptionId });
      alert("Dit abonnement vil blive opsagt ved udgangen af den nuværende periode.");
      setShowCancelModal(false);
    } catch (err) {
      console.error('Error canceling subscription:', err);
      alert("Kunne ikke opsige abonnement. Prøv igen senere.");
    }
  };

  const subscriptionDetails = {
    premium: {
      label: 'Månedligt',
      price: '180'
    },
    basic: {
      label: 'Årligt',
      price: '150'
    }
  };

  const currentPlan = userSubscription ? subscriptionDetails[userSubscription] : null;

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="grow">
      <div className="p-6 space-y-6">
        <section>
          <div>
            <div className="mb-6">
              <div className="text-sm text-gray-500 font-medium mb-2">
                Dit nuværende abonnement
              </div>
              <div className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                {currentPlan 
                  ? `${currentPlan.label} abonnement` 
                  : 'Intet aktivt abonnement'}
              </div>
            </div>

            {currentPlan && (
              <div className="relative col-span-full xl:col-span-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/60 shadow-sm rounded-b-lg max-w-md">
                <div className="absolute top-0 left-0 right-0 h-0.5 bg-green-500" aria-hidden="true"></div>
                <div className="px-5 pt-5 pb-6 border-b border-gray-200 dark:border-gray-700/60">
                  <header className="flex items-center mb-2">
                    <div className="w-6 h-6 rounded-full shrink-0 bg-green-500 mr-3">
                      <svg className="w-6 h-6 fill-current text-white" viewBox="0 0 24 24">
                        <path d="M12 17a.833.833 0 01-.833-.833 3.333 3.333 0 00-3.334-3.334.833.833 0 110-1.666 3.333 3.333 0 003.334-3.334.833.833 0 111.666 0 3.333 3.333 0 003.334 3.334.833.833 0 110 1.666 3.333 3.333 0 00-3.334 3.334c0 .46-.373.833-.833.833z" />
                      </svg>
                    </div>
                    <h3 className="text-lg text-gray-800 dark:text-gray-100 font-semibold">IV investor</h3>
                  </header>
                  <div className="text-sm mb-2">
                    Få adgang til aktie analyser, indlæg, samt aktie analyse værktøjet
                  </div>
                  <div className="text-gray-800 dark:text-gray-100 font-bold mb-4">
                    <span className="text-2xl">DKK </span>
                    <span className="text-3xl">{currentPlan.price}</span>
                    <span className="text-gray-500 font-medium text-sm">/{currentPlan.label}</span>
                  </div>
                </div>
                <div className="px-5 pt-4 pb-5">
                  <div className="text-xs text-gray-800 dark:text-gray-100 font-semibold uppercase mb-4">
                    Hvad er inkluderet
                  </div>
                  <ul>
                    <li className="flex items-center py-1">
                      <svg className="w-3 h-3 shrink-0 fill-current text-green-500 mr-2" viewBox="0 0 12 12">
                        <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                      </svg>
                      <div className="text-sm">Aktie analyse værktøjet</div>
                    </li>
                    <li className="flex items-center py-1">
                      <svg className="w-3 h-3 shrink-0 fill-current text-green-500 mr-2" viewBox="0 0 12 12">
                        <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                      </svg>
                      <div className="text-sm">Forum af Investorer</div>
                    </li>
                    <li className="flex items-center py-1">
                      <svg className="w-3 h-3 shrink-0 fill-current text-green-500 mr-2" viewBox="0 0 12 12">
                        <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                      </svg>
                      <div className="text-sm">Ai analyser</div>
                    </li>
                    <li className="flex items-center py-1">
                      <svg className="w-3 h-3 shrink-0 fill-current text-green-500 mr-2" viewBox="0 0 12 12">
                        <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                      </svg>
                      <div className="text-sm">Nyhedsfeedet</div>
                    </li>
                    <li className="flex items-center py-1">
                      <svg className="w-3 h-3 shrink-0 fill-current text-green-500 mr-2" viewBox="0 0 12 12">
                        <path d="M10.28 1.28L3.989 7.575 1.695 5.28A1 1 0 00.28 6.695l3 3a1 1 0 001.414 0l7-7A1 1 0 0010.28 1.28z" />
                      </svg>
                      <div className="text-sm">Og meget mere på vej</div>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <footer>
        <div className="flex flex-col px-6 py-5 border-t border-gray-200 dark:border-gray-700/60">
          <div className="flex self-end">
            {currentPlan && (
              <button
                className="btn bg-red-500 text-white hover:bg-red-600 ml-3"
                onClick={() => setShowCancelModal(true)}
              >
                Opsig abonnement
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <>
          <div 
            className="fixed inset-0 bg-gray-900 bg-opacity-50 z-40"
            onClick={() => setShowCancelModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Bekræft opsigelse af abonnement
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Er du sikker på at du vil opsige dit abonnement? Dit abonnement vil være aktivt indtil udgangen af din nuværende periode.
              </p>
              <div className="flex justify-end space-x-4">
                <button
                  className="btn bg-gray-300 hover:bg-gray-400 text-gray-800"
                  onClick={() => setShowCancelModal(false)}
                >
                  Annuller
                </button>
                <button
                  className="btn bg-red-500 hover:bg-red-600 text-white"
                  onClick={handleCancelSubscription}
                >
                  Bekræft opsigelse
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default PlansPanel;